import { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";

// Las 8 opciones exactas del instrumento oficial MRCV — pregunta 13,
// "Motivo No carné".
export const MOTIVOS_NO_CARNET = [
  "No ha sido vacunado",
  "Se perdió",
  "Lo tiene la promotora o vacunadora de la zona",
  "Lo tiene familiar no disponible en el momento",
  "Lo tiene la EPS al que está afiliado",
  "Lo tiene la ESE o IPS que vacuna la zona",
  "Se encuentra en el hogar infantil del ICBF o jardín",
  "Lo tiene el programa familias en acción",
];

const VACIO = {
  nombre_madre: "", documento_madre: "", telefono: "",
  regimen: "", aseguradora: "", etnia: "",
  condicion_desplazamiento: "NO",
  fecha_encuesta: new Date().toISOString().split("T")[0],
  fecha_nacimiento: "",
  motivo_no_carnet: "",
};

/**
 * Modal exclusivo — aparece cuando un menor de 6 años queda marcado
 * como "No presenta carnet" en el barrido PAI. Pide los mismos datos
 * que el MRCV normal (madre/acudiente, afiliación, fechas), pero SIN
 * el esquema de vacunación (no hay carnet del que sacarlo) — en su
 * lugar, pide el motivo por el que no lo presenta.
 *
 * `menor` trae lo que ya se llenó en el paso de PAI (nombre, documento,
 * edad) — no se vuelve a pedir.
 */
export default function MrcvSinCarnetModal({ show, onClose, menor, catalogos, onGuardar }) {
  const [form, setForm] = useState(VACIO);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) { setForm(VACIO); setErrors({}); }
  }, [show, menor?.numero_identificacion]);

  const set = (campo, valor) => setForm(p => ({ ...p, [campo]: valor }));

  const guardar = () => {
    const e = {};
    if (!form.fecha_encuesta) e.fecha_encuesta = true;
    if (!form.fecha_nacimiento) e.fecha_nacimiento = true;
    if (!form.nombre_madre.trim()) e.nombre_madre = true;
    if (!form.documento_madre.trim()) e.documento_madre = true;
    if (!form.regimen) e.regimen = true;
    if (!form.aseguradora) e.aseguradora = true;
    if (!form.motivo_no_carnet) e.motivo_no_carnet = true;
    setErrors(e);
    if (Object.keys(e).length > 0) {
      Swal.fire({ icon: "warning", title: "Faltan datos por completar", timer: 2200, showConfirmButton: false });
      return;
    }

    // Misma forma que una entrada MRCV normal (nombre_nino, documento_nino
    // + el resto de columnas de mrcv_registros) — pero tiene_carnet:"NO",
    // motivo_no_carnet lleno, y todo el esquema (fechas/verificado de cada
    // biológico) en null, porque no hay carnet del que sacarlo.
    const entrada = {
      nombre_nino: menor.nombre,
      documento_nino: menor.numero_identificacion || null,
      tiene_carnet: "NO",
      ...form,
    };
    onGuardar(entrada);
  };

  if (!menor) return null;

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>📋 MRCV — {menor.nombre}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 12 }}>
          Este menor tiene 5 años o menos y no presentó carnet ni validación PAIWEB. Se registra igual su MRCV, con el motivo por el que no lo presenta — así no hace falta una revisita solo para esto.
        </div>

        <div className="row g-2 mb-2">
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Nombre del niño/a</Form.Label>
            <Form.Control size="sm" value={menor.nombre} disabled />
          </div>
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Documento</Form.Label>
            <Form.Control size="sm" value={menor.numero_identificacion || "Sin documento"} disabled />
          </div>
        </div>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Fecha de nacimiento</Form.Label>
            <Form.Control size="sm" type="date" max={new Date().toISOString().split("T")[0]} isInvalid={!!errors.fecha_nacimiento} value={form.fecha_nacimiento} onChange={e => set("fecha_nacimiento", e.target.value)} />
          </div>
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Fecha de aplicación</Form.Label>
            <Form.Control size="sm" type="date" isInvalid={!!errors.fecha_encuesta} value={form.fecha_encuesta} onChange={e => set("fecha_encuesta", e.target.value)} />
          </div>
        </div>

        <p className="fw-bold mt-3 mb-2" style={{ fontSize: 12.5 }}>Madre / acudiente</p>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Nombre</Form.Label>
            <Form.Control size="sm" isInvalid={!!errors.nombre_madre} value={form.nombre_madre} onChange={e => set("nombre_madre", e.target.value)} />
          </div>
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Documento</Form.Label>
            <Form.Control size="sm" isInvalid={!!errors.documento_madre} value={form.documento_madre} onChange={e => set("documento_madre", e.target.value)} />
          </div>
        </div>
        <Form.Label style={{ fontSize: 11 }}>Teléfono</Form.Label>
        <Form.Control size="sm" className="mb-2" value={form.telefono} onChange={e => set("telefono", e.target.value)} />

        <p className="fw-bold mt-3 mb-2" style={{ fontSize: 12.5 }}>Afiliación</p>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Régimen</Form.Label>
            <Form.Select size="sm" isInvalid={!!errors.regimen} value={form.regimen} onChange={e => set("regimen", e.target.value)}>
              <option value="">Selecciona...</option>
              {(catalogos?.["regimenes-afiliacion"] || []).map(op => <option key={op} value={op}>{op}</option>)}
            </Form.Select>
          </div>
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>EPS</Form.Label>
            <Select
              placeholder="Buscar EPS..."
              options={(catalogos?.eps || []).map(op => ({ value: op, label: op }))}
              value={form.aseguradora ? { value: form.aseguradora, label: form.aseguradora } : null}
              onChange={sel => set("aseguradora", sel?.value || "")}
              isClearable menuPlacement="auto" menuPosition="fixed"
              styles={{
                control: (base) => ({ ...base, minHeight: 31, fontSize: 13.5, borderColor: errors.aseguradora ? "#dc3545" : base.borderColor }),
                menuPortal: (base) => ({ ...base, zIndex: 3000 }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </div>
        <Form.Label style={{ fontSize: 11 }}>Pertenencia étnica</Form.Label>
        <Select
          placeholder="Buscar etnia..."
          options={(catalogos?.etnias || []).map(op => ({ value: op, label: op }))}
          value={form.etnia ? { value: form.etnia, label: form.etnia } : null}
          onChange={sel => set("etnia", sel?.value || "")}
          isClearable menuPlacement="auto" menuPosition="fixed"
          styles={{
            control: (base) => ({ ...base, minHeight: 31, fontSize: 13.5 }),
            menuPortal: (base) => ({ ...base, zIndex: 3000 }),
          }}
          menuPortalTarget={document.body}
          className="mb-2"
        />

        <Form.Label style={{ fontSize: 11 }}>¿Condición de desplazamiento?</Form.Label>
        <div className="d-flex gap-2 mb-2">
          {["SI", "NO"].map(v => (
            <Button key={v} type="button" size="sm" className="flex-grow-1"
              variant={form.condicion_desplazamiento === v ? (v === "SI" ? "warning" : "outline-secondary") : "outline-secondary"}
              onClick={() => set("condicion_desplazamiento", v)}>
              {v}
            </Button>
          ))}
        </div>

        <p className="fw-bold mt-3 mb-2" style={{ fontSize: 12.5, color: "#b3261e" }}>13. Motivo No carné</p>
        <Form.Select size="sm" isInvalid={!!errors.motivo_no_carnet} value={form.motivo_no_carnet} onChange={e => set("motivo_no_carnet", e.target.value)}>
          <option value="">Selecciona el motivo...</option>
          {MOTIVOS_NO_CARNET.map((m, i) => <option key={m} value={m}>{i + 1}. {m}</option>)}
        </Form.Select>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>Cancelar</Button>
        <Button variant="success" size="sm" onClick={guardar}>✓ Guardar MRCV</Button>
      </Modal.Footer>
    </Modal>
  );
}